import { FC, useEffect } from 'react';
import { useAnimatedHeight } from '../../../utils/useAnimateHeight';
import { useTranslation } from '../../../utils/useTranslation';
import { FormControl } from '../../input/FormControl';
import { FormControlLabel, LabelPlacement } from '../../input/FormControlLabel';
import { Input } from '../../input/Input';
import { Radio } from '../../input/Radio';
import { Switch } from '../../input/Switch';
import { Box } from '../../layout/Box';
import { DataTestId, DataTestIdWrapper } from '../../layout/DataTestIdWrapper';
import { Typography } from '../../typography/Typography';
import { CollectionChooseNameForm } from '../CollectionChooseNameForm/CollectionChooseNameForm';
import { Wrapper } from './styles';
import { CollectionInfoContentProps } from './types';

export const CollectionInfoContent: FC<CollectionInfoContentProps> = ({ values, handlers }) => {
    const t = useTranslation();
    const {
        collectionName,
        isPublic,
        showInFeed,
        suggestsLoading,
        suggests,
        currentSuggest,
        canShowInFeed,
    } = values;
    const { onInputChange, onIsPublicChange, onShowInFeedChange } = handlers;

    const {
        contentRef: wrapperContentRef,
        containerRef: wrapperContainerRef,
        animate: animateWrapper,
    } = useAnimatedHeight();
    const { contentRef, containerRef, animate } = useAnimatedHeight();

    useEffect(() => {
        animateWrapper();
    }, [animateWrapper, collectionName, isPublic, showInFeed]);

    useEffect(() => {
        animate();
    }, [animate, suggestsLoading, suggests.length, currentSuggest, showInFeed]);

    return (
        <>
            <Box width="100%" height="100%" p={2} pb={0}>
                <Wrapper ref={wrapperContainerRef} style={{ overflow: 'hidden', height: 0 }}>
                    <div ref={wrapperContentRef}>
                        <FormControlLabel
                            label={
                                <>
                                    <Typography>{t('Публичная коллекция')}</Typography>
                                    <Typography variant="caption" color="secondary">
                                        {t('Доступна по ссылке после проверки')}
                                    </Typography>
                                </>
                            }
                            control={
                                <DataTestIdWrapper dataTestId={DataTestId.PUBLIC_COLLECTION_RADIO}>
                                    <Radio checked={isPublic} onChange={onIsPublicChange} />
                                </DataTestIdWrapper>
                            }
                            labelPlacement={LabelPlacement.LEFT}
                            labelVerticalPadding={1.5}
                            labelWidth={{ xs: '80%', sm: '90%' }}
                        />
                        {canShowInFeed ? (
                            <FormControlLabel
                                label={
                                    <>
                                        <Typography>{t('Показывать коллекцию в ленте')}</Typography>
                                        <Typography
                                            maxWidth="390px"
                                            variant="caption"
                                            color="secondary"
                                        >
                                            {t(
                                                'Другие пользователи увидят вашу коллекцию в ленте рекомендаций, когда в ней будет от 4 постов',
                                            )}
                                        </Typography>
                                    </>
                                }
                                control={
                                    <Switch checked={showInFeed} onChange={onShowInFeedChange} />
                                }
                                labelVerticalPadding={1.5}
                                labelPlacement={LabelPlacement.LEFT}
                            />
                        ) : null}
                    </div>
                </Wrapper>
                <Wrapper>
                    <FormControlLabel
                        label={
                            <>
                                <Typography>{t('Секретная коллекция')}</Typography>
                                <Typography variant="caption" color="secondary">
                                    {t('Доступна только вам')}
                                </Typography>
                            </>
                        }
                        control={
                            <DataTestIdWrapper dataTestId={DataTestId.SECRET_COLLECTION_RADIO}>
                                <Radio checked={!isPublic} onChange={onIsPublicChange} />
                            </DataTestIdWrapper>
                        }
                        labelPlacement={LabelPlacement.LEFT}
                        labelVerticalPadding={1.5}
                        labelWidth={{ xs: '80%', sm: '90%' }}
                    />
                </Wrapper>

                <div style={{ overflow: 'hidden', height: 0, width: '100%' }} ref={containerRef}>
                    <div ref={contentRef}>
                        {showInFeed && (values.suggestsLoading || values.currentSuggest) ? (
                            <CollectionChooseNameForm values={values} handlers={handlers} />
                        ) : (
                            <FormControl
                                pb={1}
                                pt={collectionName ? 2 : '30px'}
                                width="100%"
                                label={collectionName ? t('Введите название коллекции') : undefined}
                            >
                                <Input
                                    placeholder={t('Введите название коллекции')}
                                    value={collectionName}
                                    type="text"
                                    autoComplete="off"
                                    maxLength={25}
                                    onChange={onInputChange}
                                    dataTestId={DataTestId.COLLECTION_NAME_INPUT}
                                    controlDisabled={showInFeed}
                                />
                            </FormControl>
                        )}
                    </div>
                </div>
            </Box>
        </>
    );
};
